import Quill from "quill";
import { Delta } from "@vueup/vue-quill";
import type { Ref } from "vue";

type DictationSelection = {
  index: number;
  length: number;
};

type DictationSegment = {
  index: number;
  originalLength: number;
  renderedText: string;
  lastResultCount: number;
};

type SpeechRecognitionConstructor = new () => any;

type UseWordProcessorDictationOptions = {
  editorRef: Ref<any>;
  selectionRange: Ref<DictationSelection | undefined>;
  showError?: (message: string) => Promise<void> | void;
};

const INTERIM_RENDER_DEBOUNCE_MS = 75;

export const useWordProcessorDictation = ({
  editorRef,
  selectionRange,
  showError = (message: string) => useBaseToast(message, "error"),
}: UseWordProcessorDictationOptions) => {
  const isRecording = ref(false);
  const isStopping = ref(false);
  const activeSegment = ref<DictationSegment | undefined>();
  const committedResultCount = ref(0);
  const isApplyingDictationChange = ref(false);
  const userInteractedSinceLastResult = ref(false);
  const recognition = shallowRef<any>();
  const unregisterQuillListeners = shallowRef<(() => void) | undefined>();
  const pendingInterimRenderTimer = shallowRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const pendingInterimResults = shallowRef<any>();

  const getSpeechRecognitionConstructor = ():
    | SpeechRecognitionConstructor
    | undefined => {
    if (!process.client) return undefined;

    const speechWindow = window as any;
    return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
  };

  const isSpeechRecognitionSupported = computed(
    () => getSpeechRecognitionConstructor() != undefined
  );

  const quill = computed(() => editorRef.value?.getQuill?.());

  const isDisabled = computed(
    () => !isSpeechRecognitionSupported.value || quill.value == undefined
  );

  const tooltip = computed(() => {
    if (!isSpeechRecognitionSupported.value) {
      return "Dictation is not supported in this browser";
    }

    if (quill.value == undefined) {
      return "Dictation unavailable";
    }

    if (isStopping.value) {
      return "Processing Dictation";
    }

    return isRecording.value ? "Stop Dictation" : "Start Dictation";
  });

  const getCurrentSelection = (): DictationSelection | undefined => {
    const activeQuill = quill.value;
    if (!activeQuill) return selectionRange.value;

    return activeQuill.getSelection(true) ?? selectionRange.value;
  };

  const clearPendingInterimRender = (commitPendingResults = false) => {
    if (
      commitPendingResults &&
      pendingInterimResults.value &&
      activeSegment.value
    ) {
      activeSegment.value.lastResultCount = Math.max(
        activeSegment.value.lastResultCount,
        pendingInterimResults.value.length
      );
    }

    if (pendingInterimRenderTimer.value) {
      clearTimeout(pendingInterimRenderTimer.value);
    }

    pendingInterimRenderTimer.value = null;
    pendingInterimResults.value = undefined;
  };

  const resetDictation = () => {
    clearPendingInterimRender();
    unregisterQuillListeners.value?.();
    unregisterQuillListeners.value = undefined;
    recognition.value = undefined;
    isRecording.value = false;
    isStopping.value = false;
    activeSegment.value = undefined;
    committedResultCount.value = 0;
    isApplyingDictationChange.value = false;
    userInteractedSinceLastResult.value = false;
  };

  const normalizeTranscriptText = (text: string) => {
    return text.replace(/\s+/g, " ").trim();
  };

  const buildTranscriptFromResults = (results: any, startIndex = 0) => {
    const transcriptParts: string[] = [];

    for (let index = startIndex; index < results.length; index++) {
      const transcript = normalizeTranscriptText(
        results[index]?.[0]?.transcript ?? ""
      );

      if (transcript) {
        transcriptParts.push(transcript);
      }
    }

    return normalizeTranscriptText(transcriptParts.join(" "));
  };

  const hasFinalResult = (results: any, startIndex = 0) => {
    for (let index = startIndex; index < results.length; index++) {
      if (results[index]?.isFinal) {
        return true;
      }
    }

    return false;
  };

  const createSegmentFromSelection = (
    selection: DictationSelection | undefined
  ): DictationSegment | undefined => {
    if (!selection) return undefined;

    return {
      index: selection.index,
      originalLength: selection.length,
      renderedText: "",
      lastResultCount: committedResultCount.value,
    };
  };

  const registerQuillListeners = () => {
    const activeQuill = quill.value;
    if (!activeQuill) return;

    const markUserInteraction = (_event?: any, _oldEvent?: any, source?: any) => {
      if (isApplyingDictationChange.value || source == Quill.sources.SILENT) {
        return;
      }

      clearPendingInterimRender(true);
      userInteractedSinceLastResult.value = true;
    };

    activeQuill.on?.("text-change", markUserInteraction);
    activeQuill.on?.("selection-change", markUserInteraction);

    unregisterQuillListeners.value = () => {
      activeQuill.off?.("text-change", markUserInteraction);
      activeQuill.off?.("selection-change", markUserInteraction);
    };
  };

  const commitActiveSegmentAtCurrentSelection = () => {
    if (activeSegment.value) {
      committedResultCount.value = activeSegment.value.lastResultCount;
    }

    activeSegment.value = createSegmentFromSelection(getCurrentSelection());
    userInteractedSinceLastResult.value = false;
  };

  const renderTranscript = async (results: any) => {
    const activeQuill = quill.value;
    if (!activeQuill || results.length == 0) return;

    if (userInteractedSinceLastResult.value) {
      commitActiveSegmentAtCurrentSelection();
    }

    const text = buildTranscriptFromResults(
      results,
      committedResultCount.value
    );
    const segment = activeSegment.value;

    if (!segment) return;
    if (text.length == 0) {
      if (segment.renderedText.length > 0) {
        segment.lastResultCount = results.length;
      }
      return;
    }
    if (
      text == segment.renderedText ||
      normalizeTranscriptText(text) ==
        normalizeTranscriptText(segment.renderedText)
    ) {
      segment.lastResultCount = results.length;
      return;
    }

    const lengthToReplace =
      segment.renderedText.length > 0
        ? segment.renderedText.length
        : segment.originalLength;

    const delta = new Delta().retain(segment.index);

    if (lengthToReplace > 0) {
      delta.delete(lengthToReplace);
    }

    delta.insert(text);
    isApplyingDictationChange.value = true;

    try {
      activeQuill.updateContents(delta, Quill.sources.USER);
      segment.renderedText = text;
      segment.originalLength = 0;
      segment.lastResultCount = results.length;
      activeQuill.setSelection(
        segment.index + text.length,
        0,
        Quill.sources.SILENT
      );
    } finally {
      isApplyingDictationChange.value = false;
    }
  };

  const stopDictation = () => {
    if (!recognition.value || isStopping.value) return;

    isStopping.value = true;
    clearPendingInterimRender();
    unregisterQuillListeners.value?.();
    unregisterQuillListeners.value = undefined;
    recognition.value.stop();
  };

  const startDictation = async () => {
    if (isDisabled.value) {
      await showError(
        isSpeechRecognitionSupported.value
          ? "Dictation is unavailable until the editor is ready."
          : "Dictation is not supported in this browser."
      );
      return;
    }

    const SpeechRecognition = getSpeechRecognitionConstructor();
    if (!SpeechRecognition) return;

    committedResultCount.value = 0;
    isApplyingDictationChange.value = false;
    userInteractedSinceLastResult.value = false;
    activeSegment.value = createSegmentFromSelection(getCurrentSelection());
    registerQuillListeners();

    const speechRecognition = new SpeechRecognition();
    speechRecognition.continuous = true;
    speechRecognition.interimResults = true;
    speechRecognition.lang = navigator.language || "en-US";

    speechRecognition.onresult = async (event: any) => {
      if (hasFinalResult(event.results, committedResultCount.value)) {
        clearPendingInterimRender();
        await renderTranscript(event.results);
        return;
      }

      clearPendingInterimRender();
      pendingInterimResults.value = event.results;
      pendingInterimRenderTimer.value = setTimeout(() => {
        const results = pendingInterimResults.value;
        clearPendingInterimRender();

        if (results) {
          void renderTranscript(results);
        }
      }, INTERIM_RENDER_DEBOUNCE_MS);
    };

    speechRecognition.onerror = async (event: any) => {
      const isPermissionError =
        event?.error == "not-allowed" || event?.error == "service-not-allowed";

      await showError(
        isPermissionError
          ? "Microphone permission is required for dictation."
          : "Dictation could not start."
      );
      resetDictation();
    };

    speechRecognition.onend = async () => {
      if (
        (isRecording.value || isStopping.value) &&
        committedResultCount.value == 0 &&
        (activeSegment.value?.renderedText.length ?? 0) == 0
      ) {
        await showError("No speech was captured.");
      }

      resetDictation();
    };

    recognition.value = speechRecognition;

    try {
      speechRecognition.start();
      isRecording.value = true;
    } catch (error) {
      await showError("Dictation could not start.");
      resetDictation();
    }
  };

  const toggleDictation = async () => {
    if (isRecording.value) {
      stopDictation();
      return;
    }

    await startDictation();
  };

  const abortDictation = () => {
    if (recognition.value && isRecording.value) {
      clearPendingInterimRender();
      unregisterQuillListeners.value?.();
      unregisterQuillListeners.value = undefined;
      recognition.value.abort?.();
    }
  };

  return {
    isRecording,
    isStopping,
    isDisabled,
    tooltip,
    recognition,
    toggleDictation,
    abortDictation,
  };
};
